import { useEffect, useState } from 'react';
import { ApiError } from './api';

/**
 * Carregamento de um recurso da API com os TRES estados que importam.
 *
 * O que este hook proibe e o quarto estado, o que causa dano: "carregou, deu
 * erro, e a tela mostra zero". Erro nunca degrada para valor — quem consome
 * recebe `kind: 'error'` e mostra o motivo, nunca um numero.
 *
 * A extracao de mensagem vem de `lib/api.ts` e nao e reimplementada aqui: o
 * playbook registra um bug que sobreviveu ao proprio conserto porque a regra
 * estava duplicada em dois caminhos de fetch.
 */

export type Resource<T> =
  | { readonly kind: 'loading' }
  | {
      readonly kind: 'error';
      readonly code: string;
      readonly message: string;
      readonly correlationId: string | null;
      readonly httpStatus: number;
    }
  | { readonly kind: 'ready'; readonly data: T };

export function toResourceError<T>(error: unknown): Resource<T> {
  if (error instanceof ApiError) {
    return {
      kind: 'error',
      code: error.code,
      message: error.message,
      correlationId: error.correlationId,
      httpStatus: error.httpStatus,
    };
  }
  return {
    kind: 'error',
    code: 'NETWORK_ERROR',
    message: 'Não foi possível falar com a API do plano de controle.',
    correlationId: null,
    httpStatus: 0,
  };
}

/**
 * `deps` existe porque a tela de detalhe troca de id sem desmontar: sem isso a
 * rota `/conteudo/:id` mostraria o conteudo ANTERIOR ao navegar entre dois itens.
 */
export function useResource<T>(load: () => Promise<T>, deps: readonly unknown[]): Resource<T> {
  const [state, setState] = useState<Resource<T>>({ kind: 'loading' });

  useEffect(() => {
    let cancelled = false;
    setState({ kind: 'loading' });
    (async () => {
      try {
        const data = await load();
        if (!cancelled) setState({ kind: 'ready', data });
      } catch (error) {
        if (!cancelled) setState(toResourceError<T>(error));
      }
    })();
    return () => {
      cancelled = true;
    };
    // `load` fica FORA das dependencias de proposito: e uma closure recriada a
    // cada render, e inclui-la refaria a busca em loop infinito. Quem dispara a
    // recarga e `deps`, que quem chama controla.
  }, deps);

  return state;
}
